import React from 'react'
import type { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import type { Project } from '@ztechapps/common.contract'
import { CloneProjectModeEnum } from '@ztechapps/common.contract'
import { useError } from '@ztechapps/common.hook'
import { useCloneProject } from '@ztechapps/project.common'
import { Field, FormModal } from '@ztechapps/webui.form'
import { CheckboxInput, RadioInputGroup } from '@ztechapps/webui.input'
import { Alert } from '@ztechapps/webui.util'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { Stack } from 'react-bootstrap'

export type CloneProjectModalProps = {
  source: Project
  trigger: (props: ModalTriggerProps) => ReactElement | null
  onSuccess: (newProject: Project) => void
}

type CloneProjectFormValues = {
  mode: CloneProjectModeEnum
  copyTasks: boolean
  copyAssets: boolean
}

export const CloneProjectModal = ({ source, trigger, onSuccess }: CloneProjectModalProps) => {
  const [cloneProject, { error }] = useCloneProject()
  useError(error)

  const initialValues: CloneProjectFormValues = {
    mode: CloneProjectModeEnum.IndependentCopy,
    copyTasks: false,
    copyAssets: false,
  }

  const onSubmit = async (values: CloneProjectFormValues) => {
    const newProject = await cloneProject({
      sourceProjectId: source?._id,
      mode: values.mode,
      copyTasks: values.copyTasks,
      copyAssets: values.copyAssets,
    })

    if (!newProject) {
      throw new Error('Clone project mutation returned no project.')
    }

    onSuccess(newProject)
  }

  const modeOptions = [
    { value: CloneProjectModeEnum.IndependentCopy, label: 'Clone as new project' },
    { value: CloneProjectModeEnum.SubProject, label: `Create as sub-project of ${source?.code ?? ''}` },
  ]

  return (
    <FormModal
      trigger={trigger}
      triggerIcon="clone"
      title="Clone project"
      saveText="Clone"
      saveCancelButtonsProps={{ saveIcon: 'clone' }}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <Stack gap={3}>
        <Alert type="info">
          <div>
            <b>Source:</b> {source?.code} &mdash; &ldquo;{source?.name}&rdquo;
          </div>
        </Alert>

        <Field
          name="mode"
          component={RadioInputGroup}
          label="Mode"
          options={modeOptions}
          help="Choose whether the clone is an independent project or a sub-project of the source."
        />

        <CopyFlagsFields />
      </Stack>
    </FormModal>
  )
}

// Small inline form section so CheckboxInput receives `value` / `onChange`
// directly (renders label inline next to the checkbox per Bootstrap
// Form.Check). Wrapping in `Field` would put the label on a separate line
// above the checkbox, which is wrong for boolean toggles.
const CopyFlagsFields = () => {
  const { values, setFieldValue } = useFormikContext<CloneProjectFormValues>()

  return (
    <div>
      <div className="form-label">Copy from source</div>
      <CheckboxInput
        label="Tasks"
        checked={values.copyTasks}
        onChange={(checked: boolean) => setFieldValue('copyTasks', checked)}
      />
      <CheckboxInput
        label="Assets"
        checked={values.copyAssets}
        onChange={(checked: boolean) => setFieldValue('copyAssets', checked)}
      />
    </div>
  )
}
